import React, { PropsWithChildren, useState, useEffect, useCallback } from 'react';
import { camelCase, startCase } from 'lodash';
import { EntityNameColumn } from '@components/entity-name-column';
import OfflinePinIcon from '@material-ui/icons/OfflinePin';
import {
  Avatar,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useMemo } from 'react';
import { useCompanyLoader } from 'services/company-dataloader.service';
import { AvatarGroup, Skeleton } from '@material-ui/lab';
import CategoryIcon from '@material-ui/icons/Category';
import LanguageIcon from '@material-ui/icons/Language';
import DomainIcon from '@material-ui/icons/Domain';
import { DealDetail, DealEntity, dealHasOne, getEntityOrContentName } from '@lib/deals';
import { usePersonLoader } from 'services/person-dataloader.service';
import { useContentLoader } from 'services/content-dataloader.service';
import { isValid } from 'date-fns';
import EventIcon from '@material-ui/icons/Event';
import { EntityIcon } from '@components/entity-type-icon';
import { CellProps } from 'react-table';
import { EntityType } from '@lib/deals';
import { ListItem, ListItemAvatar, ListItemText } from '@material-ui/core';
import { usePintMaster, createControlField } from '@lib/common';
import { TextField } from '@material-ui/core';
import { blue, red } from '@material-ui/core/colors';
import { Autocomplete } from '@material-ui/lab';
import styled from 'styled-components';
import { PersonMaster, PersonFormValues, EmailItem } from '@lib/person-management';
import { ColoredChipIconButton } from '@components/colored-elements';
import { TrashFill } from '@icon/react-icons';
import { CompanyBasicFormField } from '@pages/company-mapping/basic-field';
import { AddCircleOutline } from '@material-ui/icons';
import { useController } from 'react-hook-form';

const CountryField = ({ value, onChange }) => {
  const [countryNames, setCountryNames] = useState([]);
  const { loading, master: countries } = usePintMaster('countries');

  useEffect(() => {
    if (!loading && countries) {
      setCountryNames(countries?.map(({ name }) => name));
    }
  }, [loading]);
  return (
    <CountryStyles>
      <Autocomplete
        value={value}
        onChange={(evt, v) => onChange && onChange(v)}
        loading={loading}
        disabled={loading}
        options={countryNames || []}
        getOptionLabel={value => value}
        renderInput={props => (
          <TextField
            label={loading ? 'Country (Loading...)' : 'Country'}
            placeholder={loading ? 'Country (Loading...)' : 'Country'}
            fullWidth
            variant="outlined"
            {...props}
          />
        )}
      />
    </CountryStyles>
  );
};

const CountryStyles = styled.div`
  .MuiChip-root {
    background: ${blue[600]};
    color: white;
  }
  .MuiChip-deleteIcon {
    color: white;
  }
`;

export const OriginCountryField = createControlField(CountryField, '');

type EnumObject = { label: React.ReactNode; value: any };
type EnumMap = EnumObject[];

/**
 * Convert a string based enum into objects
 * and redable labels.
 *
 * @param enum_ The enum to convert
 */
export const createEnumMap = (enum_: Record<string, any>, retainSpecialChars = false): EnumMap =>
  Object.values(enum_).map(value => ({
    label: retainSpecialChars ? value : startCase(camelCase(value)),
    value,
  }));

/**
 * Render a enum map to Select options.
 * @param enumMap The Enum map
 */
export const EnumsToOptions = (enumMap: EnumMap) => (
  <>
    {enumMap.map(v => (
      <option key={v.value} value={v.value}>
        {v.label}
      </option>
    ))}
  </>
);

/**
 * Create a small subtitle, used for Entity Names.
 */
function SmallSubTitle({ name, icon, display = 'block' }) {
  return (
    <span style={{ color: 'inherit', fontSize: 11, display, alignItems: 'center' }}>
      {icon}&nbsp;{name}
    </span>
  );
}

const skeleton = <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />;
/**
 * Gets the company details from the server and shows the
 * country and the primary type of the company.
 */
function ShowCompanyDetails({ id }: { id: string }) {
  const { loading, data: company, error } = useCompanyLoader(id);
  const companyType = company && company.company_types[0]?.name;
  return (
    <>
      {loading && (
        <>
          {skeleton}
          {skeleton}
        </>
      )}
      {!loading && company && (
        <>
          <SmallSubTitle
            name={company.hqcountry}
            icon={<LanguageIcon style={{ fontSize: '1em' }} />}
          />
          {companyType && (
            <SmallSubTitle
              name={companyType}
              icon={<CategoryIcon style={{ fontSize: '1em' }} color="primary" />}
            />
          )}
        </>
      )}
    </>
  );
}

/**
 * Gets the person details from the server.
 */
function ShowPersonDetails({ id }: { id: string }) {
  const { loading, data: person, error } = usePersonLoader(id);
  const personDepartment = person && person.role_id;
  return (
    <>
      {loading && (
        <>
          <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />
        </>
      )}
      {!loading && person && (
        <>
          <SmallSubTitle
            name={personDepartment}
            icon={<DomainIcon style={{ fontSize: '1em' }} />}
          />
        </>
      )}
    </>
  );
}

function ShowContentDetails({ id }: { id: string }) {
  const { loading, data: content, error } = useContentLoader(id);
  const finished = !loading && content;
  const release_date = finished && new Date(content.release_date);
  const year = finished && isValid(release_date) ? release_date.getFullYear() : 'NA';

  return (
    <>
      {loading && (
        <>
          <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />
        </>
      )}
      {finished && (
        <>
          {content.content_type && (
            <>
              <SmallSubTitle
                display="inline-flex"
                name={content.content_type}
                icon={<CategoryIcon style={{ fontSize: '1em' }} color="primary" />}
              />
              &nbsp;
            </>
          )}
          {content.lang_names && (
            <>
              <SmallSubTitle
                display="inline-flex"
                name={content.lang_names}
                icon={<LanguageIcon style={{ fontSize: '1em' }} />}
              />
              &nbsp;
            </>
          )}
          {year && (
            <SmallSubTitle
              display="inline-flex"
              name={year}
              icon={<EventIcon style={{ fontSize: '1em' }} />}
            />
          )}
        </>
      )}
      {error && <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />}
    </>
  );
}

export const SingleBuyerSellerCell = ({
  name: entityName,
  vtrId: entityVtrId,
  type: entityType,
  white = false,
  avatarImg = null,
}: {
  name: React.ReactNode;
  vtrId?: string;
  type: string;
  white?: boolean;
  avatarImg?: string;
}) => {
  const secondaryProps = useMemo(() => (white ? { color: 'inherit' } : {}), [white]);
  let Details: any;

  if (entityType === EntityType.company) {
    Details = ShowCompanyDetails;
  } else if (entityType === EntityType.person) {
    Details = ShowPersonDetails;
  }
  return entityName || entityVtrId ? (
    <>
      <EntityNameColumn
        name={entityName}
        type={entityType}
        secondary={
          entityVtrId ? (
            Details ? (
              <Details id={entityVtrId} />
            ) : (
              <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
            )
          ) : null
        }
        secondaryTypographyProps={secondaryProps}
        avatarImg={avatarImg}
      />
    </>
  ) : null;
};

export const SingleContentCell = ({
  name,
  vtrId = null,
  white = false,
}: {
  name: React.ReactNode;
  vtrId?: string;
  white?: boolean;
  type?: any; // Ignore for typescript.
}) => {
  const secondaryProps = useMemo(() => (white ? { color: 'inherit' } : {}), [white]);
  return name ? (
    <EntityNameColumn
      name={name}
      type="CONTENT"
      secondary={vtrId ? <ShowContentDetails id={vtrId} /> : null}
      secondaryTypographyProps={secondaryProps}
    />
  ) : (
    <EmptyEntityNameColumn
      name={name}
      type="CONTENT"
      secondary={vtrId ? <ShowContentDetails id={vtrId} /> : null}
      secondaryTypographyProps={secondaryProps}
    />
  );
};

export const CellForMultiEntity =
  (type: 'buyer' | 'seller' | 'content') =>
  (props: PropsWithChildren<CellProps<DealDetail, DealEntity[]>>) => {
    const entities = props.value || [];

    const RenderComponent = type === 'content' ? SingleContentCell : SingleBuyerSellerCell;

    if (!entities.length) {
      return null;
    }

    const firstEntityName = getEntityOrContentName(entities[0]) || 'no name found';

    return dealHasOne(type, props.row.original) ? (
      <RenderComponent name={firstEntityName} vtrId={entities[0].vtr_id} type={entities[0].type} />
    ) : (
      <>
        <RenderComponent
          name={
            <>
              {firstEntityName}{' '}
              <Typography color="textSecondary" variant="body2">
                +{entities.length - 1}
              </Typography>
            </>
          }
          // vtrId={buyers[0].vtr_id}
          type={entities[0].type}
        />

        <AvatarGroup>
          {entities.slice(1).map((entity, idx) => (
            <Avatar key={`entity-${idx}`} style={smallAvatar}>
              <Tooltip
                title={
                  <RenderComponent
                    name={getEntityOrContentName(entity)}
                    vtrId={entity.vtr_id}
                    type={entity.type}
                    white
                  />
                }>
                <EntityIcon noTooltip type={entity.type} />
              </Tooltip>
            </Avatar>
          ))}
        </AvatarGroup>
      </>
    );
  };
const smallAvatar = { height: '1.1em', width: '1.1em' };

export const EmptyEntityNameColumn = ({
  type,
  name,
  secondary = null,
  iconProps = {},
  hideTooltip = false,
  secondaryTypographyProps = {},
}) => {
  const entity = (
    <ListItem style={{ padding: 0 }} component="div">
      <ListItemAvatar style={{ display: 'flex', minWidth: 0, marginRight: 6 }}>
        <EntityIcon noTooltip type={type as EntityType} {...iconProps} disabled />
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="body2" style={{ fontStyle: 'italic' }}>
            Select a content
          </Typography>
        }
        secondary={secondary}
        secondaryTypographyProps={secondaryTypographyProps}
      />
    </ListItem>
  );
  return hideTooltip ? entity : <Tooltip title={type}>{entity}</Tooltip>;
};

export function parsePersonName(person: any) {
  // If any structured name already exists, do not override
  if (person.firstname || person.lastname || person.middlename) {
    return {
      firstname: person.firstname ?? null,
      middlename: person.middlename ?? null,
      lastname: person.lastname ?? null,
    };
  }

  if (!person.name || typeof person.name !== 'string') {
    return {
      firstname: null,
      middlename: null,
      lastname: null,
    };
  }

  const nameParts = person.name.trim().split(/\s+/);

  if (nameParts.length === 1) {
    return {
      firstname: nameParts[0],
      middlename: null,
      lastname: null,
    };
  }

  if (nameParts.length === 2) {
    return {
      firstname: nameParts[0],
      middlename: null,
      lastname: nameParts[1],
    };
  }

  return {
    firstname: nameParts[0],
    middlename: nameParts.slice(1, -1).join(' '),
    lastname: nameParts[nameParts.length - 1],
  };
}

export function mapApiToForm(data: PersonMaster): PersonFormValues {
  const emails: EmailItem[] = [];

  // Primary email → emails[0]
  if (data.email_id) {
    emails.push({
      email_id: data.email_id,
      email_status: data.email_status ?? null,
      is_email_generic: data.is_email_generic ?? null,
      is_email_personal: data.is_email_personal ?? null,
      //   is_email_verified: data.is_email_verified ?? false,
    });
  }

  // Secondary → emails[1+]
  if (data.secondary_emails?.length) {
    emails.push(...data.secondary_emails);
  }

  return { emails };
}
export function segregateEmails(emails: EmailForm[]): EmailPayload {
  //console.log("Segregating emails:", emails);

  // No emails OR only empty email row
  if (!emails?.length || !emails.some(e => e?.email_id)) {
    return {
      email_id: null,
      email_status: "NULL",
      is_email_verified: null,
      is_email_generic: null,
      is_email_personal: null,
      secondary_emails: [],
    };
  }

  const primary = emails.find(e => e.is_primary && e.email_id);

  // If primary missing but some email exists → fallback to first valid
  const resolvedPrimary = primary || emails.find(e => e.email_id);

  const secondary = emails.filter(
    e => e !== resolvedPrimary && e.email_id
  );

  return {
    email_id: resolvedPrimary?.email_id ?? null,
    email_status: resolvedPrimary?.email_status ?? "NULL",

    is_email_verified:
      resolvedPrimary?.email_status === "VERIFIED"
        ? true
        : resolvedPrimary?.email_status
        ? false
        : null,

    is_email_generic: toBoolOrNull(resolvedPrimary?.is_email_generic),
    is_email_personal: toBoolOrNull(resolvedPrimary?.is_email_personal),

    secondary_emails: secondary.map(e => ({
      email_id: e.email_id,
      email_status: e.email_status ?? "NULL",
      is_email_generic: toBoolOrNull(e.is_email_generic),
      is_email_personal: toBoolOrNull(e.is_email_personal),
      vtr_id: e.vtr_id ?? null,
    })),
  };
}

const toBoolOrNull = (v: boolean | 'true' | 'false' | null | undefined | ''): boolean | null => {
  if (v === true) return true;
  if (v === false) return false;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
};

type EmailForm = {
  email_id: string;
  email_status: string | '';
  is_email_generic: boolean | '';
  is_email_personal: boolean | '';
  is_primary: boolean;
  vtr_id?: string | null;
};

type SecondaryEmail = {
  email_id: string;
  email_status: string | null;
  is_email_generic: boolean | null;
  is_email_personal: boolean | null;
  vtr_id?: string | null;
};

type EmailPayload = {
  email_id?: string;
  email_status?: string | null;
  is_email_verified?: boolean | null;
  is_email_generic?: boolean | null;
  is_email_personal?: boolean | null;
  secondary_emails: SecondaryEmail[];
};

export function extractThirdPartyIds(thirdparty_ids: any[] = [], fieldName: string) {
  return thirdparty_ids
    .filter(item => item.thirdparty_id_field_name === fieldName)
    .map(item => ({
      value: item.thirdparty_id,
      vtr_id: item.vtr_id ?? null,
    }));
}

export function buildIdFormArray(
  primaryValue: any,
  secondary: { value: any; vtr_id?: number }[],
  key: string,
) {
  const arr = [];

  if (primaryValue) {
    arr.push({
      [key]: primaryValue,
      vtr_id: null,
    });
  }

  secondary.forEach(item => {
    arr.push({
      [key]: item.value,
      vtr_id: item.vtr_id ?? null,
    });
  });

  if (!arr.length) {
    arr.push({ [key]: '', vtr_id: null });
  }
  return arr;
}

export function reconcileVtrIds(existing: any[], updatedValues: any[], key: string) {
  const reusable = existing.map(item => item.vtr_id).filter(Boolean);

  return updatedValues.map(item => ({
    [key]: item,
    vtr_id: reusable.shift() ?? null,
  }));
}

export function formatThirdPartyIds(ids: any[], fieldName: string, parseNumber = true) {
  return ids
    .map(item => item?.[fieldName])
    .filter(Boolean)
    .map(value => ({
      thirdparty_id: parseNumber ? parseInt(value, 10) : value,
      thirdparty_id_field_name: fieldName,
    }));
}

export const useDynamicIds = (initialArray = []) => {
  const [indexes, setIndexes] = useState(Array.from(Array(initialArray.length || 1), (_, i) => i));

  const [counter, setCounter] = useState(initialArray.length || 1);
  const [deletedVtrIds, setDeletedVtrIds] = useState<string[]>([]);

  const add = useCallback(() => {
    setIndexes(prev => [...prev, counter]);
    setCounter(prev => prev + 1);
  }, [counter]);

  const remove = useCallback((index, dataArray) => {
    const vtrId = dataArray?.[index]?.vtr_id;
    if (vtrId) {
      setDeletedVtrIds(prev => [...prev, vtrId]);
    }

    setIndexes(prev => prev.filter(i => i !== index));
  }, []);

  return {
    indexes,
    add,
    remove,
    deletedVtrIds,
  };
};

export const DynamicIdsSection = ({
  title,
  fieldName,
  idKey,
  labelPrimary,
  labelSecondary,
  dataArray = [],
  indexes,
  add,
  remove,
  control
}) => {
  return (
    <Grid item xs={12}>
      <Grid container justify="space-between" alignItems="center">
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <IconButton onClick={add} aria-label="add" style={{ color: 'blue' }}>
          <AddCircleOutline fontSize="large" />
        </IconButton>
      </Grid>

      <Grid container spacing={1} style={{ display: 'flex', flexDirection: 'row' }}>
        {indexes.map(index => (
          <Grid
            container
            key={index}
            spacing={2}
            alignItems="center"
            xs={3}
            style={{
              marginTop: 1,
              padding: 1,
              borderRadius: 1,
            }}>
            <Grid item xs={8}>
              <CompanyBasicFormField
                name={`${fieldName}[${index}].${idKey}`}
                defaultValue={dataArray[index]?.[idKey] || ''}
                fullWidth
                label={index === 0 ? labelPrimary : labelSecondary}
              />

            <ControlledHiddenField
  name={`${fieldName}[${index}].vtr_id`}
  control={control}
/>
            </Grid>

            {indexes.length > 1 && (
              <Grid item xs={2}>
                <Tooltip title="Delete TMDB ID">
                  <ColoredChipIconButton
                    onClick={() => remove(index, dataArray)}
                    pointer
                    colored={red[900]}
                    size="small"
                    icon={<TrashFill />}
                  />
                </Tooltip>
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

export const ControlledHiddenField = ({ name, control }) => {
const { field } = useController({ name, control });

  return <input type="hidden" {...field} />;
};
/* const reconcileIds = (array = [], idKey, vtrPool = []) => {
  const pool = [...vtrPool]; // clone
console.log(idKey,array,pool)

  return (array || [])
    .filter(Boolean)
    .map(item => {
      let vtr = item?.vtr_id;

      if (!vtr && pool.length) {
        vtr = pool.shift(); // reuse next available vtr_id
      }

      return {
        [idKey]: item?.[idKey],
        ...(vtr ? { vtr_id: vtr } : {})
      };
    });
}; */
const reconcileIds = (array = [], idKey, vtrPool = []) => {
  const items = (array || []).filter(Boolean);

  // primary item (index 0)
  const primaryItem = items[0];

  // collect used ids only from secondary items
  const usedIds = items
    .slice(1)
    .map(item => item?.vtr_id)
    .filter(Boolean);

  // base available pool
  let availablePool = vtrPool.filter(id => !usedIds.includes(id));

  // if primary had vtr_id → make it reusable
  if (primaryItem?.vtr_id) {
    availablePool = [...availablePool, primaryItem.vtr_id];
  }

  // remove duplicates
  availablePool = [...new Set(availablePool)];

  return items.map((item, index) => {

    // PRIMARY → no vtr_id in payload
    if (index === 0) {
      return {
        [idKey]: item?.[idKey]
      };
    }

    // untouched secondary
    if (item?.vtr_id) {
      return {
        [idKey]: item?.[idKey],
        vtr_id: item.vtr_id
      };
    }

    // reuse id
    const reused = availablePool.shift();

    return {
      [idKey]: item?.[idKey],
      ...(reused ? { vtr_id: reused } : {})
    };
  });
};
/* 
const reconcileEmails = (emails = [], vtrPool = []) => {
  const pool = [...vtrPool];
console.log(emails,pool)
  return (emails || [])
    .filter(Boolean)
    .map(email => {
            // Primary email → never attach vtr_id
      if (email?.is_primary) {
        return {
          email_id: email?.email_id,
          email_status: email?.email_status,
          is_email_generic: email?.is_email_generic,
          is_email_personal: email?.is_email_personal,
          is_primary: true
        };
      }
      let vtr = email?.vtr_id;

      if (!vtr && pool.length) {
        vtr = pool.shift();
      }

      return {
        email_id: email?.email_id,
        email_status: email?.email_status,
        is_email_generic: email?.is_email_generic,
        is_email_personal: email?.is_email_personal,
        is_primary: email?.is_primary,
        ...(vtr ? { vtr_id: vtr } : {})
      };
    });
};
 */const reconcileEmails = (emails = [], vtrPool = []) => {
  const items = (emails || []).filter(Boolean);

  // collect used vtr_ids ONLY from secondary emails
  const usedIds = items
    .filter(e => !e?.is_primary)
    .map(e => e?.vtr_id)
    .filter(Boolean);

  // primary vtr_ids should return to pool
  const primaryIds = items
    .filter(e => e?.is_primary)
    .map(e => e?.vtr_id)
    .filter(Boolean);

  const availablePool = [
    ...vtrPool.filter(id => !usedIds.includes(id)),
    ...primaryIds
  ].filter((id, i, arr) => arr.indexOf(id) === i); // remove duplicates

  return items.map(email => {

    // PRIMARY → never send vtr_id
    if (email?.is_primary) {
      return {
        email_id: email?.email_id,
        email_status: email?.email_status,
        is_email_generic: email?.is_email_generic,
        is_email_personal: email?.is_email_personal,
        is_primary: true
      };
    }

    // SECONDARY WITH EXISTING vtr_id
    if (email?.vtr_id) {
      return email;
    }

    // SECONDARY WITHOUT vtr_id → reuse
    const reused = availablePool.shift();

    return {
      email_id: email?.email_id,
      email_status: email?.email_status,
      is_email_generic: email?.is_email_generic,
      is_email_personal: email?.is_email_personal,
      is_primary: false,
      ...(reused ? { vtr_id: reused } : {})
    };
  });
};
export const buildRelationPayload = (relation, pools) => {
  return {
    ...relation,
  emails: reconcileEmails(
      relation.emails,
      pools.email
    ),
    tmdb_ids: reconcileIds(
      relation.tmdb_ids,
      "tmdb_id",
      pools.tmdb
    ),

    imdb_ids: reconcileIds(
      relation.imdb_ids,
      "imdb_id",
      pools.imdb
    ),

    hubspot_ids: reconcileIds(
      relation.hubspot_ids,
      "hubspot_id",
      pools.hubspot
    ),
  };
};